import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useResolve } from './use';
import { TOKEN_ILogger, type ILogger } from '@/services/Logger';

// The last ring of INVARIANTS rule 17. The store catches what it can roll back from and toasts;
// the view catches ValidationError and renders it inline; whatever escapes both lands here. It is
// the safety net, not the design: a failure that reaches this screen is a bug.
//
// React error boundaries can only be class components, so the function wrapper exists to keep DI
// (useResolve) outside the class.

interface BoundaryProps {
  logger: ILogger;
  children: ReactNode;
}

// Exported for `ErrorBoundaryView.test.tsx`, which hands it a CaptureLogger as a prop. The wrapper
// below is the only part that touches DI, and reaching into the module-global container from a
// test is banned (INVARIANTS rule 11).
export class RawErrorBoundary extends Component<BoundaryProps, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.logger.error('render error', info.componentStack, error);
  }

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children;
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-lg font-semibold">Something went wrong.</h1>
        <pre className="max-w-2xl overflow-auto rounded-lg bg-neutral-900 p-3 text-left text-xs text-neutral-200 ring-1 ring-neutral-800">
          {this.state.error.message}
        </pre>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-lg bg-neutral-800 px-4 py-2 text-sm hover:bg-neutral-700"
        >
          Reload
        </button>
      </div>
    );
  }
}

export function ErrorBoundaryView({ children }: { children: ReactNode }) {
  const logger = useResolve(TOKEN_ILogger);
  return <RawErrorBoundary logger={logger}>{children}</RawErrorBoundary>;
}
